import {
  Box,
  Button,
  IconButton,
  TextField,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { Row, Col } from "react-bootstrap";
import {
  ColDef,
  GridReadyEvent,
  IServerSideDatasource,
  IServerSideGetRowsParams,
} from "ag-grid-enterprise";
import PushPinIcon from "@mui/icons-material/PushPin";
import ExcelIcon from "../../assets/images/ExcelExport.png";
import CloseIcon from "@mui/icons-material/Close";
import { UserGridPreferenceModel } from "../../types/UserGridPreferenceModel";
import {
  getUserGridPreference,
  saveUserGridPreference,
} from "../../api/userDataApi/userGridPreferenceAPI";
import NavigationBar from "../NavigationBar/NavigationBarComponent";
import "ag-grid-enterprise/styles/ag-grid.css";
import "ag-grid-enterprise/styles/ag-theme-quartz.min.css";
import { useLoader } from "../../context/LoaderContext";
import CustomLoadingOverlay from "../CustomLoadingOverlay/CustomLoadingOverlay ";
import { useSnackbar } from "../Common/SnackbarContext";
import { appSettings } from "../../config/appSetting";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { AssetArchiveModelInfo } from "../../types/ArchiveSearchModel";
import { getArchiveSearchData } from "../../api/archiveSearchApi/archiveSearchApi";
import { FormatDate, FormatDateWithoutTime } from "../Common/CommonMethods";
import moment from "moment";
import LocalStorageUtil from "../../utils/localStorage";
import BlobPathCellRenderer from "./BlobPathCellRenderer";

interface BlobPathRendererProps {
  value: string;
  onPopupCenter: (url: string, title: string, w: number, h: number) => void;
}

interface LinkButtonRendererProps {
  data: any;
  onClickOpen: (data: any) => void;
}

const LinkButtonRenderer: React.FC<LinkButtonRendererProps> = ({
  data,
  onClickOpen,
}) => {
  if (!data) {
    return null; // Return null instead of an empty string for cleaner JSX
  }

  return (
    <button
      type="button"
      style={{
        color: "var(--aggrid-text-color)",
        background: "none",
        border: "none",
        padding: 0,
        cursor: "pointer",
        textDecoration: "underline",
        font: "inherit",
      }}
      onClick={() => onClickOpen(data)}
      aria-label="View Metadata"
    >
      View Metadata
    </button>
  );
};

const ArchiveSearch = () => {
  const gridRef = useRef<AgGridReact>(null);
  const [open, setOpen] = useState(false);
  const [rowData, setRowData] = useState<any[]>([]);
  const [searchValue] = useState<string | null>(null);
  const [gridRecordExist, setGridRecordExist] = useState(false);
  const [searchContent, setSearchContent] = useState("");
  const [_searchContentError, set_SearchContentError] = useState<string>(""); // NOSONAR
  const [_showIconButton, set_ShowIconButton] = useState<boolean>(false); // NOSONAR
  const [_resetShow, set_ResetShow] = useState<boolean>(false); // NOSONAR
  const [gridApi, setGridApi] = useState<any>();
  const txtSearchValueRef = useRef(searchValue);
  const [jsonPayloadValue, setJsonPayloadValue] = useState<string>("");
  const [copyValue, setCopyValue] = useState(false);
  const [_cacheBlockSize, set_cacheBlockSize] = useState(10);
  const pageSizeSelector = appSettings.agGridPageSizeSelector;
  const defaulPageSize = appSettings.agGridDefaulPageSize;
  const [showLoader, setShowLoader] = useState(false);
  const [_totalCount, set_TotalCount] = useState<number>(0); // NOSONAR
  const { showSnackbar } = useSnackbar();
  
  //Popup center function
  const popupCenter = (url: string, title: string, w: number, h: number) => {
    const dualScreenLeft = window.screenLeft ?? window.screenX ?? 0;
    const dualScreenTop = window.screenTop ?? window.screenY ?? 0;

    const width =
      window.innerWidth ||
      document.documentElement.clientWidth ||
      window.screen.width;

    const height =
      window.innerHeight ||
      document.documentElement.clientHeight ||
      window.screen.height;

    const left = width / 3 - w / 3 + dualScreenLeft;
    const top = height / 3 - h / 3 + dualScreenTop;
    const newWindow = window.open(
      url,
      title,
      `readonly=true, scrollbars=yes, width=${w}, height=${h}, top=${top}, left=${left}`
    );
    if (newWindow) {
      newWindow.focus();
    }
  };

  const handleClickOpen = (props: any) => {
    const parsedMetaData = JSON.parse(props.metaData);
    setJsonPayloadValue(parsedMetaData);
    setOpen(true);
  };

  const linkButtonCellRenderer = (params: { data: any }) => (
    <LinkButtonRenderer data={params.data} onClickOpen={handleClickOpen} />
  );

  const blobPathCellRenderer = (params: { value: string }) => (
    <BlobPathCellRenderer value={params.value} onPopupCenter={popupCenter} />
  );

  //Defined grid column
  const [columnDef] = useState<ColDef[]>([
    {
      headerName: "#",
      width: 75,
      sortable: false,
      filter: false,
      resizable: false,
      colId: "col1",
      valueGetter: (params) => {
        return params.node ? (params.node.rowIndex ?? 0) + 1 : 0;
      },
      cellStyle: () => {
        return { borderright: "1px solid #dee2e6" };
      },
    },
    {
      headerName: "Document ID",
      field: "documentID",
      width: 180,
      hide: false,
      colId: "col2",
      enableRowGroup: true,
    },
    {
      headerName: "BusinessUnitName",
      field: "businessUnitName",
      width: 180,
      hide: false,
      colId: "col3",
      enableRowGroup: true,
    },
    {
      headerName: "Rendition",
      field: "rendition",
      width: 180,
      hide: false,
      colId: "col4",
      enableRowGroup: true,
    },
    {
      headerName: "CUSIP",
      field: "cusip",
      width: 180,
      hide: false,
      colId: "col5",
      enableRowGroup: true,
    },
    {
      headerName: "DocType",
      field: "docType",
      width: 180,
      hide: false,
      colId: "col6",
      enableRowGroup: true,
    },
    {
      headerName: "DocDate",
      field: "docDate",
      width: 180,
      hide: false,
      colId: "col7",
      enableRowGroup: true,
      valueFormatter: (params: any) => FormatDateWithoutTime(params.value),
    },
    {
      headerName: "BlobPath",
      field: "blobPath",
      cellRenderer: blobPathCellRenderer,
      width: 180,
      hide: false,
      colId: "col8",
      enableRowGroup: true,
    },
    {
      headerName: "MetaData",
      field: "metaData",
      cellRenderer: linkButtonCellRenderer,
      pinned: "right",
      lockPinned: true,
      suppressSizeToFit: true,
      width: 160,
      minWidth: 160,
      hide: false,
      colId: "colmetaData",
      sortable: false,
      filter: false,
      resizable: false,
      enableRowGroup: false,
    },
    {
      headerName: "CreatedDate",
      field: "createdDate",
      width: 180,
      hide: false,
      colId: "col10",
      enableRowGroup: true,
      valueFormatter: (params: any) => FormatDate(params.value),
    },
    {
      headerName: "SubSystemName",
      field: "subSystemName",
      width: 180,
      hide: false,
      colId: "col11",
      enableRowGroup: true,
    },
  ]);

  const getData = async (searchTerm?: string) => {
    setShowLoader(true);
    try {
      const params: AssetArchiveModelInfo = {
        NoOfRecords: 1000,
        CurrentPage: 1,
        SortColumn: "",
        SortOrder: 0,
        IsExportToExcel: true,
        Timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        ContextName: LocalStorageUtil.getItem("contextName") ?? "",
        SearchTerm: searchTerm ?? "",
      };
      const archiveSearchResult = await getArchiveSearchData(params);

      // If API returns the array directly
      const results = archiveSearchResult?.metadata ?? [];
      const totalRecords = archiveSearchResult?.count ?? results.length;

      setRowData(results);
      set_TotalCount(totalRecords);
      setGridRecordExist(results.length > 0);

      if (results.length === 0) {
        setGridRecordExist(false);
        showSnackbar("0 records found");
      } else {
        setGridRecordExist(true);
        showSnackbar("Grid state applied successfully.");

        const gridPreferences = await getUserGridPreferences();
        if (gridPreferences) {
          applyGridPreferences(gridPreferences);
        }
      }
      if (gridApi) {
        clearSelectionState();
        gridApi.onFilterChanged();
      }
    } catch (error) {
      console.error("Archive search error:", error);
      showSnackbar("Error fetching data", true);
    } finally {
      setShowLoader(false);
    }
  };

  const getUserGridPreferences = async () => {
    setShowLoader(true);
    const userGridPreference = {
      Area: "ArchiveSearch",
      UserID: Number(LocalStorageUtil.getItem("userId")),
      BusinessUnitID: Number(LocalStorageUtil.getItem("businessUnitId")),
      ContextName: LocalStorageUtil.getItem("contextName")?.toString(),
    };

    try {
      const archiveSearchDetails = await getUserGridPreference(
        userGridPreference
      );
      if (archiveSearchDetails) {
        showSnackbar("Grid state successfully saved.");
      }
      return archiveSearchDetails;
    } catch (error) {
      console.error("Error fetching data", error);
    } finally {
      setShowLoader(false);
    }
  };

  //Apply grid preferences
  const applyGridPreferences = (gridPreferences: any) => {
    if (gridPreferences) {
      try {
        const gridState = JSON.parse(gridPreferences);
        if (gridRef.current) {
          gridRef.current.api.applyColumnState({
            state: gridState,
            applyOrder: true,
          });
        }
      } catch (error) {
        console.error("Error parsing grid preferences:", error);
      }
    } else {
      console.warn("No grid preferences found or GridBlob is undefined.");
    }
  };
  // Export data to excel
  const handleExport = () => {
    if (gridRef.current) {
      gridRef.current.api.exportDataAsExcel({
        fileName:
          "ArchiveSearchReports_" + moment().format("YYYYMMDD") + ".xlsx",
        sheetName: "ArchiveSearch Report",
        columnKeys: columnDef
          .map((colDef) => colDef.colId)
          .filter(
            (colId): colId is string =>
              colId !== undefined && colId !== "undefined"
          ),
        processCellCallback: function (params) {
          return params.value;
        },
      });
    }
  };

  useEffect(() => {
    txtSearchValueRef.current = searchContent;
  }, [searchContent]);

  //Reapply grid state on component update
  useEffect(() => {
    const reapplyGridState = async () => {
      const gridPreferences = await getUserGridPreferences();
      if (gridPreferences) {
        applyGridPreferences(gridPreferences);
      }
    };
    reapplyGridState();
  }, [rowData]);

  //Save grid preferences
  const handleSaveGridPreferences = async () => {
    setShowLoader(true);
    if (gridRef.current) {
      const gridState = gridRef.current.api.getColumnState();
      const userGridPreference: UserGridPreferenceModel = {
        Area: "ArchiveSearch",
        UserID: Number(LocalStorageUtil.getItem("userId")),
        BusinessUnitID: Number(LocalStorageUtil.getItem("businessUnitId")),
        ContextName: LocalStorageUtil.getItem("contextName")?.toString(),
        GridBlob: JSON.stringify(gridState),
        LastModifiedBy: "DocDistro-UserGridPreferencesAPI",
        LastModifiedDate: new Date().toISOString(),
      };

      try {
        const archiveSearchDetails = await saveUserGridPreference(
          userGridPreference
        );
        if (archiveSearchDetails) {
          showSnackbar("Grid state successfully saved.");
        }
      } catch (error) {
        console.error("Error fetching data", error);
      } finally {
        setShowLoader(false);
      }
    }
  };

  const clearSelectionState = useCallback(() => {
    if (gridRef.current) {
      gridRef.current.api.setServerSideSelectionState({
        selectAll: false,
        toggledNodes: [],
      });
    }
  }, []);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchContent(event.target.value); // Update the correct state variable
    set_ResetShow(true);
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault(); // Prevent form submission
      handleSearchSubmit();
    }
  };

  const handleClose = () => {
    setCopyValue(false);
    setOpen(false);
  };

  const handleCopyToClipboard = () => {
    setCopyValue(true);
  };
  // Handel search button click
  const handleSearchSubmit = useCallback(
    async (e?: React.FormEvent) => {
      try {
        if (searchContent.trim().length > 0) {
          set_SearchContentError("");
        } else {
          set_SearchContentError("Please enter search content");
        }
        if (gridApi) {
          clearSelectionState();
          gridApi.onFilterChanged();
        }
      } catch (error) {
        console.log(error);
      }
    },
    [searchContent, gridApi, clearSelectionState]
  );

  // Handel search content clear
  const handelSearchContentClear = () => {
    setSearchContent("");
    set_SearchContentError("");
    set_ShowIconButton(false);
    try {
      if (gridApi) {
        clearSelectionState();
        gridApi.onFilterChanged();
      }
    } catch (error) {
      console.log(error);
    }
  };
  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params.api);

    const dataSource: IServerSideDatasource = {
      getRows: function (params: IServerSideGetRowsParams) {
        (async () => {
          const currentPage = params.api.paginationGetCurrentPage() + 1;
          const pageSize = params.api.paginationGetPageSize();

          const { request } = params;
          const startRow = request.startRow;
          const endRow = request.endRow;
          const { sortModel } = params.request;

          let sortOrder = 0;
          let sortColumn = "";

          if (sortModel && sortModel.length > 0) {
            const sort = sortModel[0].sort;
            const colId = sortModel[0].colId.replace(/_\d+$/, "");
            sortOrder = sort === "asc" ? 0 : 1;
            sortColumn = colId;
          }
          set_cacheBlockSize(pageSize);
          try {
            const payload: AssetArchiveModelInfo = {
              NoOfRecords: pageSize,
              CurrentPage: currentPage,
              SortColumn: sortColumn,
              SortOrder: sortOrder,
              IsExportToExcel: true,
              Timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              ContextName: LocalStorageUtil.getItem("contextName") ?? "",
              SearchTerm: txtSearchValueRef.current ?? "",
              StartRow: startRow,
              EndRow: endRow,
            };

            const archiveSearchResult = await getArchiveSearchData(payload);

            const results = archiveSearchResult?.metadata ?? [];
            const totalRecords = archiveSearchResult?.count || results.length;

            set_TotalCount(totalRecords);
            setGridRecordExist(results.length > 0);

            params.success({
              rowData: results,
              rowCount: totalRecords,
            });
          } catch (error) {
            params.success({
              rowData: [],
              rowCount: 0,
            });
            showSnackbar("No records found", true);
          } finally {
            setShowLoader(false);
          }
        })();
      },
    };

    params.api.setGridOption("serverSideDatasource", dataSource);
  };

  return (
    <div>
      <NavigationBar
        menuTitle="Search"
        screenTitle=""
        title="Archive Search"
        text="Archive Search"
      />
      <Row style={{ margin: "1em" }}>
        <Col xs={12}>
          <Box>
            <div>
              <Box
                component="form"
                sx={{ "& .MuiTextField-root": { m: 1, width: "50ch" } }}
                noValidate
                autoComplete="off"
              >
                <Box
                  sx={{
                    padding: "1em",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <TextField
                      id="txtArchiveSearch"
                      variant="outlined"
                      fullWidth
                      style={{
                        padding: "0px",
                        margin: "0px",
                        width: "300px",
                      }}
                      value={searchContent}
                      onChange={handleInputChange}
                      onKeyDown={handleSearchKeyPress}
                      InputProps={{
                        style: {
                          backgroundColor: "white",
                          height: "2.5em",
                          fontSize: "11px",
                        },
                        placeholder: "Search",
                      }}
                      title="Please enter the text to Search"
                    ></TextField>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleSearchSubmit}
                      className="button search-button"
                      sx={{ height: "2.5em", mr: 1 }}
                      style={{ marginLeft: "1.3em", height: "2.5em" }}
                    >
                      Search
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handelSearchContentClear}
                      className="button reset-button"
                      sx={{ height: "2.5em" }}
                      style={{ marginLeft: "0.5em", height: "2.5em" }}
                    >
                      Reset
                    </Button>
                  </Box>
                  {gridRecordExist && (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Tooltip title="Click to save grid state">
                        <IconButton
                          onClick={handleSaveGridPreferences}
                          className="preference-icon"
                        >
                          <PushPinIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Click to export the grid content in excel">
                        <button
                          type="button"
                          onClick={handleExport}
                          tabIndex={0}
                          aria-label="Export to Excel"
                          style={{
                                  background: "none",
                                  border: "none",
                                  padding: 0,
                                  cursor: "pointer",
                                  marginTop: "-.2em",
                                  alignItems: "center",
                                  display: "flex",
                                }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleExport();
                            }
                          }}
                        >
                          <img
                            src={ExcelIcon}
                            className="excel-icon"
                            alt="Export to Excel"
                            style={{ verticalAlign: "middle" }}
                          />
                        </button>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
              </Box>
            </div>
            <div
              className="ag-theme-quartz-dark"
              style={{ height: "72vh", width: "100%" }}
            >
            <AgGridReact
                columnDefs={columnDef}
                rowData={rowData}
                ref={gridRef}
                pagination={true}
                paginationPageSize={defaulPageSize}
                paginationPageSizeSelector={pageSizeSelector}
                headerHeight={30}
                rowGroupPanelShow="always"
                pivotPanelShow="always"
                rowModelType="serverSide"
                sideBar={{ toolPanels: ["columns", "filters"] }}
                onGridReady={onGridReady}
                cacheBlockSize={_cacheBlockSize}
                overlayNoRowsTemplate={
                  '<span class="ag-overlay-loading-center">No rows to show</span>'
                }
                loading={!rowData || showLoader}
                loadingOverlayComponent={CustomLoadingOverlay}
              />
            </div>
            <div>
              <Dialog
                open={open}
                fullWidth={true}
                maxWidth="xl"
                PaperProps={{ sx: { width: 400, height: 550 } }}
              >
                <DialogTitle
                  style={{
                    backgroundColor: "var(--primary-color) ",
                    height: "5em", // Set the background color of the dialog title
                  }}
                >
                  <div
                    className="col-sm-12"
                    style={{
                      display: "flex",
                      justifyContent: "left",
                    }}
                  >
                    <div
                      className="col-sm-11"
                      style={{
                        display: "flex",
                        alignItems: "left",
                        gap: "7px",
                        color: "var(--aggrid-text-color)",
                        fontSize: "1.5em",
                      }}
                    >
                      MetaData
                    </div>
                    <div
                      className="col-sm-1"
                      style={{
                        display: "flex",
                        alignItems: "flex-end",
                        justifyContent: "right",
                      }}
                    >
                      {" "}
                      <Tooltip title="Close">
                        <IconButton
                          onClick={handleClose}
                          style={{
                            cursor: "pointer",
                            alignItems: "flex-end",
                            color: "var(--aggrid-text-color)",
                            width: "20px",
                            marginTop: "-5px",
                          }}
                          data-testid="close-icon"
                        >
                          <CloseIcon />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>
                </DialogTitle>
                <DialogContent dividers>
                  <pre
                    style={{
                      width: "100%",
                      overflow: "auto",
                      backgroundColor: "#f9f9f9",
                      border: "1px solid #ccc",
                      font: "menu",
                      fontFamily: "monospace",
                      whiteSpace: "pre-wrap",
                      wordWrap: "break-word",
                    }}
                  >
                    {JSON.stringify(jsonPayloadValue, null, 2)}
                  </pre>
                  {copyValue ? (
                    <span style={{ color: "green", fontSize: "14px" }}>
                      {" "}
                      Copied text to clipboard.
                    </span>
                  ) : null}
                </DialogContent>
                <DialogActions>
                  <CopyToClipboard
                    text={JSON.stringify(jsonPayloadValue, null, 2)}
                    onCopy={handleCopyToClipboard}
                  >
                    <Button
                      className="copytoclipboard"
                      style={{
                        textTransform: "none",
                        borderRadius: "0px",
                        background: "rgb(77, 123, 167) !important",
                        color: "#ddd !important",
                        fontSize: "11px",
                        padding: "5px 12px",
                        border: "1px solid #144979",
                        margin: "0 5px 5px 5px",
                        whiteSpace: "white-space",
                      }}
                    >
                      Copy to clipboard
                    </Button>
                  </CopyToClipboard>
                </DialogActions>
              </Dialog>
            </div>
          </Box>
        </Col>
      </Row>
    </div>
  );
};

export default ArchiveSearch;
